import React, { useEffect, useState, ReactNode } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components'; // For styled loading spinner
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth'; // Import Firebase types
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { auth, db } from '../firebase/config'; // Your Firebase auth and Firestore instances

// --- Interfaces ---
interface PrivateRouteProps {
  children: ReactNode;
}

// --- Styled Loading Spinner ---
const LoadingOverlay = styled.div`
  position: fixed; /* Or absolute if container is relative */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8); /* Optional: semi-transparent background */
  z-index: 999; /* Ensure it's above other content */
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000; /* Spinner color */
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spinAnimation} 1s linear infinite;
`;

// --- Component ---
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false); // For business-specific routes
  const [isBusinessPending, setIsBusinessPending] = useState(false); // <<< NEW STATE for pending status
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);

  const { businessId } = useParams<{ businessId?: string }>();
  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);
    setIsAuthorized(false);
    setIsBusinessPending(false); // <<< Reset pending state
    setError(null);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setIsAuthenticated(!!user);

      if (!user) {
        setIsLoading(false);
        return;
      }

      if (!businessId) {
        // Not a business-specific route, authorized by default if logged in
        setIsAuthorized(true);
        setIsLoading(false);
        console.log("PrivateRoute: No businessId, user authorized.");
        return;
      }

      // --- Authorization & Status Check for Business Routes ---
      try {
        console.log(`PrivateRoute: Checking access for user ${user.uid}, business ${businessId}`);
        const businessDocRef = doc(db, 'businesses', businessId);
        const businessDocSnap = await getDoc(businessDocRef);

        if (!businessDocSnap.exists()) {
          setError(`Business with ID ${businessId} not found.`);
          setIsLoading(false);
          return;
        }
        const businessData = businessDocSnap.data();

        // <<<--- ADDED: Check Business Status --- >>>
        if (businessData.status === 'pending') {
          // Allow access to verify route even for pending businesses
          const isVerifyRoute = location.pathname.includes('/verify');
          
          if (!isVerifyRoute) {
            console.log(`PrivateRoute: Business ${businessId} is PENDING. Access denied.`);
            setIsBusinessPending(true); // Set pending state
            setIsAuthorized(false); // Ensure not authorized
            // Optionally set a specific error message if you want to display it
            // setError(`Access denied: Business "${businessData.name || businessId}" is currently pending approval.`);
            setIsLoading(false);
            return; // Stop further checks
          } else {
            console.log(`PrivateRoute: Business ${businessId} is PENDING, but allowing access to verify route.`);
          }
        }
        // <<<--- End Status Check --- >>>

        // Business exists and is NOT pending, proceed with authorization checks
        console.log(`PrivateRoute: Business ${businessId} status is '${businessData.status}'. Checking manager/staff...`);

        // 1. Check if the current user is the manager
        if (businessData.managerId === user.uid) {
          console.log(`PrivateRoute: User ${user.uid} IS manager.`);
          setIsAuthorized(true);
          setIsLoading(false);
          return; // Authorized as manager
        }
        console.log(`PrivateRoute: User ${user.uid} is NOT manager (Manager: ${businessData.managerId})`);

        // 2. If not manager, check if they are a staff member
        const staffCollectionRef = collection(db, 'staff');
        const staffQuery = query(
          staffCollectionRef,
          where('businessId', '==', businessId),
          where('userId', '==', user.uid),
          limit(1)
        );
        const staffQuerySnap = await getDocs(staffQuery);

        if (!staffQuerySnap.empty) {
          console.log(`PrivateRoute: User ${user.uid} IS staff member.`);
          setIsAuthorized(true); // Authorized as staff member
        } else {
          console.log(`PrivateRoute: User ${user.uid} is NOT staff member.`);
          setIsAuthorized(false); // Not authorized
        }

      } catch (err: any) {
        console.error("PrivateRoute: Error checking authorization:", err);
        setError("Failed to check authorization.");
      } finally {
        setIsLoading(false);
      }
      // --- End Authorization Check ---
    });

    return () => unsubscribe();
  }, [businessId]); // Rerun effect if businessId changes

  // --- Render Logic ---

  if (isLoading) {
    return (
      <LoadingOverlay><Spinner /></LoadingOverlay>
    );
  }

  if (error) {
    // Display general errors
    return (
      <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
        <h2>Access Error</h2>
        <p>{error}</p>
        {/* Optionally add a link back */}
        {/* <a href="/businesses">Go to My Businesses</a> */}
      </div>
    );
  }

  if (!isAuthenticated) {
    // Not logged in
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // --- Handle Pending Status Redirect ---
  // If it's a business route AND the business is pending
  if (businessId && isBusinessPending) {
      console.warn(`PrivateRoute: Redirecting user from PENDING business ${businessId}`);
      // Redirect to businesses list, maybe with a message state?
      return <Navigate to="/businesses" state={{ message: `Business ${businessId} is pending approval.` }} replace />;
  }
  // --- End Pending Status Redirect ---


  // Handle Unauthorized Access (Not manager/staff for a non-pending business)
  // If it's a business route AND user is NOT authorized (and not pending)
  if (businessId && !isAuthorized) {
     console.warn(`PrivateRoute: Redirecting UNAUTHORIZED user from ${location.pathname} for business ${businessId}`);
     return <Navigate to="/businesses" replace />; // Or "/dashboard"
  }

  // User is authenticated AND (authorized for the business OR it's not a business-specific route)
  return <>{children}</>;
};

export default PrivateRoute;